import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';
import Toast from '../components/Toast';
import { consultPackages } from '../data/mockData';

const Consult = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', company: '', projectType: '', budget: '', message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setFormData({ name: '', email: '', company: '', projectType: '', budget: '', message: '' });
  };

  return (
    <main className="pt-24 pb-0">
      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-16">
        <ScrollReveal>
          <span className="text-[#5f6368] text-xs tracking-wide uppercase">Consultation</span>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl text-[#1a1a1a] mt-4 tracking-tight max-w-3xl"
            style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
          >
            Let's bring your vision to life
          </h1>
          <p className="text-base text-[#5f6368] mt-6 max-w-2xl leading-relaxed">
            Whether you're starting from scratch or refining an existing brand, we'd love to hear about your project.
          </p>
        </ScrollReveal>
      </section>

      {/* Packages */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-24">
        <ScrollReveal>
          <span className="text-[#5f6368] text-xs tracking-wide uppercase mb-8 block">Engagement Options</span>
        </ScrollReveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {consultPackages.map((pkg, i) => (
            <ScrollReveal key={pkg.id} delay={i * 100}>
              <div
                className={`rounded-2xl p-8 border transition-shadow duration-300 h-full flex flex-col ${
                  pkg.highlighted
                    ? 'border-[#1a1a1a] shadow-lg bg-white'
                    : 'border-[#e8eaed] bg-white hover:shadow-md'
                }`}
              >
                {pkg.highlighted && (
                  <span className="text-xs font-medium bg-[#1a1a1a] text-white px-3 py-1 rounded-full self-start mb-4">
                    Most Popular
                  </span>
                )}
                <h3 className="text-xl font-medium text-[#1a1a1a] mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  {pkg.title}
                </h3>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-3xl font-medium text-[#1a1a1a]">{pkg.price}</span>
                  <span className="text-sm text-[#5f6368]">/ {pkg.duration}</span>
                </div>
                <p className="text-sm text-[#5f6368] leading-relaxed mb-6">{pkg.description}</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#3c4043]">
                      <Check size={16} className="text-[#1a1a1a] mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  className={`w-full py-3 rounded-full text-sm font-medium transition-colors duration-200 ${
                    pkg.highlighted
                      ? 'bg-[#1a1a1a] text-white hover:bg-[#333]'
                      : 'border border-[#dadce0] text-[#1a1a1a] hover:bg-[#f8f9fa]'
                  }`}
                >
                  {pkg.cta}
                </button>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="bg-[#f8f9fa] py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              <ScrollReveal>
                <h2
                  className="text-3xl md:text-4xl text-[#1a1a1a] mb-8 tracking-tight"
                  style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
                >
                  Send us a message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs text-[#5f6368] mb-1.5 block">Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-[#e8eaed] bg-white text-sm focus:outline-none focus:border-[#1a1a1a] transition-colors"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#5f6368] mb-1.5 block">Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-[#e8eaed] bg-white text-sm focus:outline-none focus:border-[#1a1a1a] transition-colors"
                        placeholder="you@company.com"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs text-[#5f6368] mb-1.5 block">Company</label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-[#e8eaed] bg-white text-sm focus:outline-none focus:border-[#1a1a1a] transition-colors"
                        placeholder="Company name"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#5f6368] mb-1.5 block">Project Type</label>
                      <select
                        value={formData.projectType}
                        onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-[#e8eaed] bg-white text-sm focus:outline-none focus:border-[#1a1a1a] transition-colors appearance-none"
                      >
                        <option value="">Select a service</option>
                        <option value="branding">Brand Identity</option>
                        <option value="web">Web Design</option>
                        <option value="print">Print Design</option>
                        <option value="packaging">Packaging</option>
                        <option value="motion">Motion Graphics</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#5f6368] mb-1.5 block">Tell us about your project *</label>
                    <textarea
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[#e8eaed] bg-white text-sm focus:outline-none focus:border-[#1a1a1a] transition-colors resize-none"
                      placeholder="What are you looking to achieve?"
                    />
                  </div>
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-[#333] transition-colors duration-200"
                  >
                    <Send size={16} /> Send Message
                  </button>
                </form>
              </ScrollReveal>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2">
              <ScrollReveal delay={200}>
                <div className="bg-white rounded-2xl p-8 border border-[#e8eaed]">
                  <h3 className="font-medium text-[#1a1a1a] mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>Get in touch</h3>
                  <div className="space-y-5">
                    <a href="mailto:hello@Grapix.studio" className="flex items-start gap-3 text-sm text-[#5f6368] hover:text-[#1a1a1a] transition-colors">
                      <Mail size={18} className="mt-0.5 shrink-0" />
                      <div>
                        <p className="font-medium text-[#1a1a1a]">Email</p>
                        <p>hello@Grapix.studio</p>
                      </div>
                    </a>
                    <div className="flex items-start gap-3 text-sm text-[#5f6368]">
                      <Phone size={18} className="mt-0.5 shrink-0" />
                      <div>
                        <p className="font-medium text-[#1a1a1a]">Phone</p>
                        <p>+1 (555) 234-5678</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 text-sm text-[#5f6368]">
                      <MapPin size={18} className="mt-0.5 shrink-0" />
                      <div>
                        <p className="font-medium text-[#1a1a1a]">Location</p>
                        <p>San Francisco, CA</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 text-sm text-[#5f6368]">
                      <Clock size={18} className="mt-0.5 shrink-0" />
                      <div>
                        <p className="font-medium text-[#1a1a1a]">Hours</p>
                        <p>Mon-Fri, 9:00 AM - 6:00 PM PST</p>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
      <Toast 
        isVisible={submitted} 
        message="Thanks! We'll be in touch within 24 hours." 
      />
    </main>
  );
};

export default Consult;
